import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { 
  Plus, TrendingUp, Clock, DollarSign, AlertCircle, Calendar,
  FileText, Users, Briefcase, BarChart3, Upload, Send,
  CheckCircle2, ArrowRight, Circle, Target, Zap, PlayCircle, Search, ChevronRight, Sparkles,
  ChevronDown, MessageSquare, FolderOpen, Activity, Shield, User, Eye
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { auth } from '../utils/auth';
import LexyAppShell from '../components/LexyAppShell';
import OutlookCalendar from '../components/calendar/OutlookCalendar';
import SplitInboxView from '../components/inbox/SplitInboxView';
import AINextSteps from '../components/AINextSteps';
import { lexyAI } from '../utils/lexyAI';
import InvitationsPanel from '../components/InvitationsPanel';
import CaseDetailModal from '../components/CaseDetailModal';
import CommunityPage from './CommunityPage';
import InvoicingView from '../components/lexy/InvoicingViewMinimal';
import ClientsView from '../components/lexy/ClientsViewWorldClass';
import TimeTrackingView from '../components/lexy/TimeTrackingViewMinimal';
import AnalyticsView from '../components/lexy/AnalyticsViewMinimal';
import SettingsView from '../components/lexy/SettingsViewWorldClass';
import OverviewView from '../components/lexy/OverviewViewMinimal';
import CasesView from '../components/lexy/CasesViewMinimal';
import CasesWithTabs from './CasesWithTabs';
import MarketplaceView from '../components/lexy/MarketplaceViewWorldClass';
import AFAOnboardingWizard from '../components/afa/AFAOnboardingWizard';
import AFAStatusBanner from '../components/afa/AFAStatusBanner';
import CaseQueueView from '../components/lexy/CaseQueueViewMinimal';
import ActivityFeedView from '../components/lexy/ActivityFeedViewMinimal';
import DocumentsViewMinimal from '../components/lexy/DocumentsViewMinimal';
import CreateCaseModal from '../components/modals/CreateCaseModal';
import { activityApi, clientsApi, tasksApi, authApi } from '../services';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LexyCommandCenter = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const activeView = searchParams.get('tab') || 'overview';
  const [tokenHandled, setTokenHandled] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);
  const [showCreateCaseModal, setShowCreateCaseModal] = useState(false);
  
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState<{
    quickStats: {
      activeCases: number;
      hoursThisWeek: number;
      pendingTasks: number;
      monthlyRevenue: number;
      marketplaceOffers: number;
      nextDeadline: any;
    };
    todaysFocus: any[];
    recentActivity: any[];
    cases: any[];
    allCases?: any[];
    clients?: any[];
    time_entries?: any[];
    unbilled_amount?: number;
  }>({
    quickStats: {
      activeCases: 0,
      hoursThisWeek: 0,
      pendingTasks: 0,
      monthlyRevenue: 0,
      marketplaceOffers: 0,
      nextDeadline: null
    },
    todaysFocus: [],
    recentActivity: [],
    cases: []
  });
  
  // States for cases view
  const [allCases, setAllCases] = useState([]);
  const [marketplaceCases, setMarketplaceCases] = useState([]);
  const [afaStatus, setAfaStatus] = useState(null); // AFA profile status
  const [afaProfile, setAfaProfile] = useState(null); // Full AFA profile data
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [caseFilter, setCaseFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState('all'); // all, owned, collaboration
  const [caseCounts, setCaseCounts] = useState({ owned: 0, collaboration: 0 });
  const [expandedCase, setExpandedCase] = useState(null);
  const [selectedCaseDetail, setSelectedCaseDetail] = useState(null);
  const [showCaseDetailModal, setShowCaseDetailModal] = useState(false);
  
  // States for clients view
  const [clients, setClients] = useState([]);
  const [clientSearchQuery, setClientSearchQuery] = useState('');
  const [clientFilter, setClientFilter] = useState('all');
  
  // States for analytics view
  const [analyticsData, setAnalyticsData] = useState(null);
  const [analyticsTimeframe, setAnalyticsTimeframe] = useState('month');
  const [sandboxStatus, setSandboxStatus] = useState(null);
  const [sandboxPolling, setSandboxPolling] = useState(false);
  
  // States for time tracking view
  const [timeEntries, setTimeEntries] = useState([]);
  const [timeSearchQuery, setTimeSearchQuery] = useState('');
  
  // States for invoicing view
  const [invoices, setInvoices] = useState([]);
  const [invoiceFilter, setInvoiceFilter] = useState('all');

  const fetchData = async () => {
    try {
      const [casesRes, workspaceRes, marketplaceRes, clientsRes, afaStatusRes] = await Promise.all([
        axios.get(`${API}/cases/my-cases/unified?filter=all`),
        axios.get(`${API}/workspace/status`).catch(() => ({ data: {} })),
        axios.get(`${API}/marketplace/cases/new`).catch(() => ({ data: { cases: [] } })),
        clientsApi.list().catch(() => ({ clients: [] })),
        axios.get(`${API}/afa/profile/status`).catch(() => ({ data: { status: null } }))
      ]);
      
      const allCases = casesRes.data.cases || [];
      const marketplaceCases = marketplaceRes.data.cases || [];
      const clientsList = Array.isArray(clientsRes) ? clientsRes : (clientsRes.clients || []);
      const afaStatusData = afaStatusRes.data?.status || null;
      
      console.log('📊 Clients data received:', clientsRes);
      console.log('📊 Clients list parsed:', clientsList);
      console.log('📊 Number of clients:', clientsList.length);
      
      setAllCases(allCases);
      setMarketplaceCases(marketplaceCases);
      setClients(clientsList);
      setAfaStatus(afaStatusData);
    } catch (error) {
      console.error('Fetch data error:', error);
    }
  };

  const refreshUserData = async () => {
    try {
      const response = await authApi.getMe();
      setUser(response);
      auth.saveUser(response);
    } catch (error) {
      console.error('Error refreshing user data:', error);
    }
  };

  // Poll sandbox status if seeding
  useEffect(() => {
    const isSandbox = localStorage.getItem('is_sandbox') === 'true';
    const sandboxTenantId = localStorage.getItem('sandbox_tenant_id');
    
    if (isSandbox && sandboxTenantId && !sandboxPolling) {
      setSandboxPolling(true);
      
      const pollStatus = async () => {
        try {
          
          const response = await axios.get(
            `${API}/admin/sandbox/status/${sandboxTenantId}`,
            
          );
          
          setSandboxStatus(response.data);
          
          if (response.data.status === 'completed') {
            // Sandbox is ready, stop polling and refresh data
            setSandboxPolling(false);
            fetchData();
          } else if (response.data.status === 'seeding') {
            // Continue polling
            setTimeout(pollStatus, 2000);
          } else {
            // Failed or unknown status
            setSandboxPolling(false);
          }
        } catch (error) {
          console.error('Sandbox status poll error:', error);
          setSandboxPolling(false);
        }
      };
      
      pollStatus();
    }
  }, []);

  useEffect(() => {
    // 🔐 Handle token from Microsoft OAuth redirect (or any OAuth)
    const tokenFromUrl = searchParams.get('token');
    
    if (tokenFromUrl && !tokenHandled) {
      console.log('🔐 Token found in URL, saving to localStorage');
      setTokenHandled(true);
      localStorage.setItem('token', tokenFromUrl);
      
      // Fetch user info with the new token
      const fetchUserWithToken = async () => {
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${tokenFromUrl}` }
          });
          if (response.data) {
            console.log('✅ User fetched from token:', response.data);
            localStorage.setItem('user', JSON.stringify(response.data));
            setUser(response.data);
            setAuthLoading(false);
            
            // Check if user has allowed role
            const allowedRoles = ['LAWYER', 'ADMIN', 'TENANT_ADMIN', 'USER'];
            if (!allowedRoles.includes(response.data.role)) {
              console.log('❌ User role not allowed:', response.data.role);
              navigate('/lexy');
              return;
            }
            
            // Remove token from URL for cleaner appearance (after successful fetch)
            const newParams = new URLSearchParams(searchParams);
            newParams.delete('token');
            newParams.delete('provider');
            newParams.delete('new_user');
            setSearchParams(newParams, { replace: true });
            
            fetchDashboardData(response.data);
          }
        } catch (err) {
          console.error('❌ Failed to fetch user from token:', err);
          setAuthLoading(false);
          navigate('/lexy/login');
        }
      };
      fetchUserWithToken();
      return;
    }
    
    // Skip normal auth check if we're handling a token
    if (tokenFromUrl || tokenHandled) {
      return;
    }
    
    const initUser = auth.getUser();
    console.log('LexyCommandCenter - User check:', initUser);
    console.log('LexyCommandCenter - User role:', initUser?.role);
    
    // Allow LAWYER, ADMIN, TENANT_ADMIN, and USER (Microsoft users) to access Lexy dashboard
    const allowedRoles = ['LAWYER', 'ADMIN', 'TENANT_ADMIN', 'USER'];
    const isAllowed = initUser && allowedRoles.includes(initUser.role);
    
    console.log('LexyCommandCenter - Is allowed:', isAllowed);
    
    if (!isAllowed) {
      console.log('LexyCommandCenter - REDIRECTING to /lexy');
      setAuthLoading(false);
      navigate('/lexy');
      return;
    }
    
    console.log('LexyCommandCenter - ACCESS GRANTED, loading dashboard');
    setUser(initUser);
    setAuthLoading(false);
    
    // Load dashboard data immediately with current user
    fetchDashboardData(initUser);
    
    // Refresh user data in background (don't call fetchDashboardData again)
    refreshUserData();
  }, [searchParams, tokenHandled]);

  const fetchDashboardData = async (userData = user) => {
    console.log('🚀 fetchDashboardData CALLED with userData:', userData);
    
    if (!userData) {
      console.error('❌ No user data available for dashboard fetch');
      return;
    }
    
    console.log('✅ userData exists, proceeding to fetch...');
    
    try {
      
      console.log('🔑 Using HttpOnly cookie for auth');
      
      // Fetch all necessary data
      console.log('📡 Starting Promise.all for data fetch...');
      const [casesRes, workspaceRes, marketplaceRes, clientsRes, userDataRes] = await Promise.all([
        axios.get(`${API}/cases/my-cases/unified?filter=all`),
        axios.get(`${API}/lexy/dashboard/${userData.id}`),
        axios.get(`${API}/marketplace/cases/new`).catch(() => ({ data: { cases: [] } })),
        axios.get(`${API}/clients`).catch(() => ({ data: [] })),
        axios.get(`${API}/auth/me`)
      ]);
      console.log('✅ Promise.all completed!');
      console.log('📦 userDataRes:', userDataRes);

      const allCases = casesRes.data.cases || [];
      const counts = casesRes.data.counts || { owned: 0, collaboration: 0 };
      const activeCases = allCases.filter(c => c.status !== 'COMPLETED' && c.status !== 'DECLINED');
      const marketplaceCases = marketplaceRes.data.cases || [];
      const clientsList = Array.isArray(clientsRes.data) ? clientsRes.data : (clientsRes.data.clients || []);
      const workspaceData = workspaceRes.data || {};
      const afaProfileData = userDataRes.data.afa_profile || {};
      const afaStatusData = afaProfileData.status || null;

      // Debug logging
      console.log('📊 Clients data received:', clientsRes.data);
      console.log('📊 Clients list parsed:', clientsList);
      console.log('📊 Number of clients:', clientsList.length);
      console.log('🔍 AFA Debug - fetchDashboardData:');
      console.log('  userDataRes.data.afa_profile:', userDataRes.data.afa_profile);
      console.log('  afaProfileData:', afaProfileData);
      console.log('  afaStatusData:', afaStatusData);

      // Store for cases view
      setAllCases(allCases);
      setCaseCounts(counts);
      setMarketplaceCases(marketplaceCases);
      setClients(clientsList);
      setAfaProfile(afaProfileData);
      setAfaStatus(afaStatusData);
      
      console.log('  ✅ setAfaStatus called with:', afaStatusData);

      // Calculate quick stats using real data from workspace API
      const quickStats = {
        activeCases: activeCases.length,
        totalClients: clientsList.length,  // Add total clients
        hoursThisWeek: workspaceData.this_week?.hours || 0,  // Use real hours from backend
        pendingTasks: workspaceData.pending_tasks_count || workspaceData.stats?.pending_tasks || countPendingTasks(allCases),
        monthlyRevenue: calculateMonthlyRevenue(allCases),
        marketplaceOffers: marketplaceCases.length,
        nextDeadline: findNextDeadline(allCases)
      };

      // Generate today's focus items
      const todaysFocus = generateTodaysFocus(allCases, marketplaceCases);

      // Get recent activity
      const recentActivity = await fetchRecentActivity();

      setDashboardData({
        quickStats,
        todaysFocus,
        recentActivity,
        cases: activeCases.slice(0, 6), // Show top 6 cases
        allCases: allCases,  // CRITICAL: Include all cases for OverviewViewNew stats
        clients: clientsList,  // Include clients list
        time_entries: workspaceData.time_entries || [],  // Include time entries
        unbilled_amount: workspaceData.unbilled_amount || 0  // Include unbilled amount
      });

    } catch (error) {
      console.error('❌ Error fetching dashboard:', error);
      console.error('❌ Error details:', error.response?.data || error.message);
      toast.error('Kunne ikke laste dashboard');
    } finally {
      console.log('🏁 fetchDashboardData FINISHED, loading set to false');
      setLoading(false);
    }
  };

  const countPendingTasks = (cases) => {
    // Count cases needing attention
    return cases.filter(c => 
      c.status === 'AWAITING_LAWYER_REVIEW' || 
      c.status === 'ASSIGNED' ||
      c.status === 'OFFER_SENT'
    ).length;
  };

  const calculateMonthlyRevenue = (cases) => {
    // Simplified calculation
    const completedThisMonth = cases.filter(c => 
      c.status === 'COMPLETED' && 
      c.completed_at && 
      new Date(c.completed_at).getMonth() === new Date().getMonth()
    );
    return completedThisMonth.length * 399;
  };

  const findNextDeadline = (cases) => {
    // Find nearest deadline - simplified
    const today = new Date();
    const upcomingCases = cases.filter((c: any) => c.deadline && new Date(c.deadline) > today);
    if (upcomingCases.length === 0) return null;
    
    upcomingCases.sort((a: any, b: any) => new Date(a.deadline).getTime() - new Date(b.deadline).getTime());
    return upcomingCases[0].deadline;
  };

  const generateTodaysFocus = (cases, marketplace) => {
    const focus = [];
    
    if (marketplace.length > 0) {
      focus.push({
        type: 'marketplace',
        title: `${marketplace.length} nye marketplace-saker`,
        action: 'Se saker',
        priority: 'high',
        link: '/lexy/dashboard?tab=marketplace'
      });
    }

    const awaitingReview = cases.filter(c => c.status === 'AWAITING_LAWYER_REVIEW');
    if (awaitingReview.length > 0) {
      focus.push({
        type: 'review',
        title: `${awaitingReview.length} saker venter på vurdering`,
        action: 'Vurder',
        priority: 'high',
        link: '/lexy/dashboard?tab=overview'
      });
    }

    const offersWaiting = cases.filter(c => c.status === 'OFFER_SENT');
    if (offersWaiting.length > 0) {
      focus.push({
        type: 'offer',
        title: `${offersWaiting.length} tilbud sendt - venter på svar`,
        action: 'Se status',
        priority: 'medium',
        link: '/lexy/dashboard?tab=overview'
      });
    }

    const inProgress = cases.filter(c => c.status === 'IN_PROGRESS');
    if (inProgress.length > 0) {
      focus.push({
        type: 'progress',
        title: `${inProgress.length} saker pågår`,
        action: 'Jobb med',
        priority: 'medium',
        link: '/lexy/dashboard?tab=overview'
      });
    }

    return focus;
  };

  const fetchRecentActivity = async () => {
    // Simplified - would fetch from activities endpoint
    return [
      { time: '10 min siden', action: 'Ny marketplace-sak: Arbeidsrett', icon: Briefcase },
      { time: '1 time siden', action: 'Registrerte 2 timer på Eiendomssak', icon: Clock },
      { time: '2 timer siden', action: 'Sendte juridisk vurdering', icon: Send },
      { time: 'I går', action: 'Opprettet ny klient: Acme AS', icon: Users }
    ];
  };

  const quickActions = [
    { 
      icon: Plus, 
      label: 'Ny sak', 
      description: 'Opprett Lexy-sak',
      action: () => setShowCreateCaseModal(true)
    },
    { 
      icon: Users, 
      label: 'Ny klient', 
      description: 'Registrer klient',
      action: () => navigate('/lexy/clients/new')
    },
    { 
      icon: Clock, 
      label: 'Registrer timer', 
      description: 'Logg arbeidstid',
      action: () => toast.info('Velg en sak for å registrere timer')
    },
    { 
      icon: Briefcase, 
      label: 'Marketplace', 
      description: 'Se nye saker',
      action: () => setSearchParams({ tab: 'marketplace' })
    },
    { 
      icon: Upload, 
      label: 'Last opp dokument', 
      description: 'Til eksisterende sak',
      action: () => toast.info('Velg en sak for å laste opp')
    },
    { 
      icon: BarChart3, 
      label: 'Rapporter', 
      description: 'Se statistikk',
      action: () => setSearchParams({ tab: 'analytics' })
    }
  ];

  const handleNavigate = (view) => {
    if (view === 'ai-workspace') {
      navigate('/lexy/ai-workspace');
    } else if (view === 'overview') {
      // Navigate to dashboard with overview tab
      navigate('/lexy/dashboard?tab=overview');
    } else {
      // Navigate to dashboard with the specified tab
      navigate(`/lexy/dashboard?tab=${view}`);
    }
  };
  
  const filterCases = (casesToFilter) => {
    let filtered = casesToFilter;
    
    // Role filter (OWNER vs EXPERT)
    if (roleFilter !== 'all') {
      if (roleFilter === 'owned') {
        filtered = filtered.filter(c => c.role === 'OWNER');
      } else if (roleFilter === 'collaboration') {
        filtered = filtered.filter(c => c.role === 'EXPERT');
      }
    }
    
    // Search filter
    if (searchQuery) {
      filtered = filtered.filter(c => 
        (c.topic && c.topic.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (c.title && c.title.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (c.description && c.description.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    }
    
    // Status filter
    if (statusFilter !== 'all') {
      if (statusFilter === 'new') {
        filtered = filtered.filter(c => c.status === 'ASSIGNED' || c.status === 'AWAITING_LAWYER_REVIEW');
      } else if (statusFilter === 'in_progress') {
        filtered = filtered.filter(c => c.status === 'IN_PROGRESS');
      } else if (statusFilter === 'completed') {
        filtered = filtered.filter(c => c.status === 'COMPLETED');
      }
    }
    
    return filtered;
  };
  
  const handleTakeCase = async (caseId) => {
    try {
      await axios.post(
        `${API}/marketplace/cases/${caseId}/take`,
        { lawyer_id: user.id }
      );
      
      toast.success('Sak tatt! Åpner workspace...');
      navigate(`/marketplace/case/${caseId}`);
      
    } catch (error) {
      console.error('Error taking case:', error);
      toast.error('Kunne ikke ta sak');
    }
  };

  if (loading) {
    return (
      <LexyAppShell activeView={activeView} onNavigate={handleNavigate}>
        <div className="flex items-center justify-center h-full">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
        </div>
      </LexyAppShell>
    );
  }

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'border-red-500 bg-red-50';
      case 'medium': return 'border-amber-500 bg-amber-50';
      case 'low': return 'border-green-500 bg-green-50';
      default: return 'border-slate-300 bg-slate-50';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'AWAITING_LAWYER_REVIEW': return 'bg-red-100 text-red-700';
      case 'ASSIGNED': return 'bg-blue-100 text-blue-700';
      case 'IN_PROGRESS': return 'bg-purple-100 text-purple-700';
      case 'OFFER_SENT': return 'bg-amber-100 text-amber-700';
      case 'COMPLETED': return 'bg-green-100 text-green-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  if (!user) {
    return (
      <LexyAppShell activeView={activeView} onNavigate={handleNavigate}>
        <div className="h-full flex items-center justify-center">
          <div className="text-slate-600">Laster...</div>
        </div>
      </LexyAppShell>
    );
  }

  return (
    <LexyAppShell activeView={activeView} onNavigate={handleNavigate}>
      <>
      {/* AFA Status Banner - shows on all views except when approved */}
      <AFAStatusBanner 
        status={afaStatus} 
        rejectionReason={afaProfile?.rejection_reason} 
      />
      
      {activeView === 'overview' ? (
        <OverviewView 
          user={user}
          dashboardData={dashboardData}
          navigate={navigate}
          onUserUpdate={refreshUserData}
        />
      ) : activeView === 'cases' ? (
        <CasesWithTabs />
      ) : activeView === 'marketplace' ? (
        // Check AFA status - show wizard ONLY if never created (null) or draft
        (() => {
          console.log('🔍 Marketplace view - AFA Status Check:', afaStatus);
          console.log('  Show wizard?', afaStatus === null || afaStatus === 'DRAFT');
          return (afaStatus === null || afaStatus === 'DRAFT');
        })() ? (
          <AFAOnboardingWizard 
            onComplete={() => {
              // Refresh to get updated status
              console.log('✅ AFAOnboardingWizard completed, refreshing...');
              fetchData();
            }}
          />
        ) : afaStatus === 'SUBMITTED' ? (
          <div className="flex flex-col items-center justify-center py-20 px-6">
            <Clock className="w-16 h-16 text-yellow-500 mb-4" />
            <h2 className="text-2xl font-bold text-slate-900 mb-2">AFA-profil under vurdering</h2>
            <p className="text-slate-600 text-center max-w-md">
              Din profil er sendt til admin for godkjenning. Du vil motta beskjed når profilen er godkjent.
            </p>
          </div>
        ) : afaStatus === 'REJECTED' ? (
          <div className="flex flex-col items-center justify-center py-20 px-6">
            <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md">
              <h2 className="text-xl font-bold text-red-900 mb-2">AFA-profil avvist</h2>
              <p className="text-red-700 mb-4">
                Din profil har blitt avvist. Vennligst gå til profil-siden for å se tilbakemelding og gjøre endringer.
              </p>
              <Button
                onClick={() => navigate('/lexy/afa/profile')}
                className="w-full bg-red-600 hover:bg-red-700"
              >
                Gå til profil
              </Button>
            </div>
          </div>
        ) : (
          <MarketplaceView
            marketplaceCases={marketplaceCases}
            navigate={navigate}
          />
        )
      ) : activeView === 'clients' ? (
        <ClientsView 
          clients={clients}
          searchQuery={clientSearchQuery}
          setSearchQuery={setClientSearchQuery}
          clientFilter={clientFilter}
          setClientFilter={setClientFilter}
          onRefresh={fetchDashboardData}
          navigate={navigate}
        />
      ) : activeView === 'documents' ? (
        // Documents View - All documents across all cases
        <DocumentsViewMinimal />
      ) : activeView === 'community' ? (
        // Community View
        <CommunityPage />
      ) : activeView === 'time' ? (
        // Time Tracking View
        <TimeTrackingView 
          cases={allCases}
          user={user}
          onRefresh={fetchDashboardData}
        />
      ) : activeView === 'invoices' ? (
        // Invoicing View
        <InvoicingView 
          cases={allCases}
          user={user}
          onRefresh={fetchDashboardData}
        />
      ) : activeView === 'analytics' ? (
        // Analytics View
        <AnalyticsView 
          cases={allCases}
          user={user}
        />
      ) : activeView === 'settings' ? (
        // Settings View
        <SettingsView 
          user={user}
          onNavigate={handleNavigate}
          onProfileUpdate={refreshUserData}
        />
      ) : activeView === 'calendar' ? (
        // Calendar View - Outlook-style with Microsoft 365 integration
        <OutlookCalendar 
          onNavigateToCase={(caseId) => navigate(`/lexy/case/${caseId}/workspace`)}
        />
      ) : activeView === 'inbox' ? (
        // Split Inbox - Outlook-style dual-pane view
        <SplitInboxView 
          onNavigateToCase={(caseId) => navigate(`/lexy/case/${caseId}/workspace`)}
        />
      ) : activeView === 'case-queue' ? (
        // Arbeidskø - Prioritized case queue
        <CaseQueueView />
      ) : activeView === 'activity-feed' ? (
        // Aktivitetsoversikt - Activity feed across all cases
        <ActivityFeedView />
      ) : (
        // Fallback
        <div className="h-full overflow-y-auto bg-slate-50">
          <div className="max-w-7xl mx-auto p-6">
            <div className="bg-white rounded-lg border border-slate-200 p-8 text-center">
              <h2 className="text-xl font-semibold text-slate-900 mb-2">
                Siden finnes ikke
              </h2>
              <Button
                onClick={() => handleNavigate('overview')}
                className="bg-slate-900 hover:bg-slate-800"
              >
                Tilbake til oversikt
              </Button>
            </div>
          </div>
        </div>
      )}
      </>
      
      {/* Create Case Modal */}
      <CreateCaseModal 
        isOpen={showCreateCaseModal}
        onClose={() => setShowCreateCaseModal(false)}
        onSuccess={(caseId) => {
          setShowCreateCaseModal(false);
          fetchDashboardData(user);
        }}
      />
    </LexyAppShell>
  );
};


export default LexyCommandCenter;
